import api from './api';

// Tag de receta (catálogo extensible: vegetariano, vegano, sin gluten, …).
// El backend devuelve este shape en GET /api/tags-receta y dentro de cada
// receta como `tags?: TagRecetaAPI[]`.
export interface TagRecetaAPI {
  id: number;
  slug: string;
  nombre: string;
  descripcion?: string | null;
  activo: number | boolean;
}

// Interfaces para las respuestas de la API
export interface ListarRecetasResponse {
  data: RecetaAPI[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface RecetaAPI {
  id: number;
  nombre: string;
  descripcion?: string;
  categoria?: string;
  tipo_comida: string;
  porciones: number;
  tiempo_preparacion: number;
  instrucciones: string;
  costo_total: number;
  estado:
    | 'borrador'
    | 'disponible'
    | 'archivada'
    | 'aprobada'
    | 'inactiva'
    | undefined
    | null;
  chef_id: number;
  activo: boolean;
  created_at: string;
  updated_at: string;
  chef?: {
    id: number;
    nombre: string;
    email: string;
  };
  usuarios?: {
    id: number;
    nombre: string;
    email: string;
  };
  ingredientes?: IngredienteRecetaAPI[];
  ingredientes_receta?: IngredienteRecetaAPI[];
  _count?: {
    ingredientes: number;
  };
  costo_por_porcion?: number;
  tags?: TagRecetaAPI[];
}

export interface IngredienteRecetaAPI {
  id: number;
  receta_id: number;
  ingrediente_id: number;
  cantidad: number;
  /** Cantidad convertida a la unidad base del ingrediente (Fase 3). */
  cantidad_base?: number | null;
  /** FK a unidades_medida de la unidad elegida por el chef (Fase 3). */
  unidad_medida_id?: number | null;
  unidad: string;
  costo_parcial: number;
  created_at: string;
  updated_at: string;
  // El backend puede enviar ingrediente (singular) o ingredientes (plural) dependiendo del endpoint
  ingrediente?: {
    id: number;
    nombre: string;
    descripcion?: string;
    categoria: string;
    unidad_medida: string;
    costo_unitario: number;
    stock_actual: number;
    stock_minimo: number;
    stock_maximo: number;
    proveedor?: string;
    activo: boolean;
    created_at: string;
    updated_at: string;
    // Sistema de unidad base (Fase 1-3)
    tipo_fisico?: 'peso' | 'volumen' | 'cantidad' | null;
    unidad_base_id?: number | null;
    unidad_base_simbolo?: string | null;
    unidad_display_id?: number | null;
    unidad_display_simbolo?: string | null;
  };
  ingredientes?: {
    id: number;
    nombre: string;
    descripcion?: string;
    categoria: string;
    unidad_medida: string;
    costo_unitario: number;
    stock_actual: number;
    stock_minimo: number;
    stock_maximo: number;
    proveedor?: string;
    activo: boolean;
    created_at: string;
    updated_at: string;
  };
  stock_disponible?: number;
  stock_suficiente?: boolean;
}

export interface CrearRecetaRequest {
  nombre: string;
  descripcion?: string;
  categoria?: 'desayuno' | 'almuerzo' | 'cena' | 'postre' | 'colacion_fria';
  tipo_comida: string;
  porciones: number;
  tiempo_preparacion: number;
  instrucciones: string;
  estado?: 'borrador' | 'disponible' | 'archivada';
  tags?: number[];
}

export interface ActualizarRecetaRequest {
  nombre?: string;
  descripcion?: string;
  categoria?: 'desayuno' | 'almuerzo' | 'cena' | 'postre' | 'colacion_fria';
  tipo_comida?: string;
  porciones?: number;
  tiempo_preparacion?: number;
  instrucciones?: string;
  estado?: 'borrador' | 'disponible' | 'archivada';
  tags?: number[];
}

export interface AgregarIngredienteRequest {
  ingrediente_id: number;
  cantidad: number;
  unidad_medida: string;
}

export interface ActualizarIngredienteRequest {
  ingrediente_id?: number;
  cantidad?: number;
  unidad_medida?: string;
}

export interface FiltrosRecetas {
  busqueda?: string;
  categoria?: 'desayuno' | 'almuerzo' | 'cena' | 'postre' | 'colacion_fria';
  tipoPlato?: string;
  estado?: 'borrador' | 'disponible' | 'archivada';
  chef_id?: number;
  activo?: boolean;
  page?: number;
  limit?: number;
}

export interface CategoriaReceta {
  value: string;
  label: string;
}

export interface TipoPlato {
  id: number;
  nombre: string;
  descripcion?: string;
  color?: string;
  orden?: number;
  activo?: boolean | number;
  filas_default?: number | null;
  // Bloque operativo del menú: 'desayuno' | 'almuerzo' | 'cena' | 'bebida'.
  // Agregado por migración 2026-06-10. Usado por el wizard de cliente para
  // agrupar las categorías de acceso por sección visual.
  bloque?: string | null;
}

export interface ApiResponse<T> {
  message?: string;
  data: T;
}

// Servicios para recetas
export const recetasAPI = {
  // GET /api/recetas - Listar recetas con filtros
  listar: async (params?: FiltrosRecetas): Promise<ListarRecetasResponse> => {
    console.log('🔍 recetasAPI.listar - Parámetros enviados:', params);

    // Mapear parámetros de camelCase a snake_case para el API
    const apiParams: any = {};
    if (params) {
      if (params.busqueda) apiParams.search = params.busqueda;
      if (params.categoria) apiParams.categoria = params.categoria;
      if (params.tipoPlato) apiParams.tipo_plato = params.tipoPlato;
      if (params.estado) apiParams.estado = params.estado;
      if (params.chef_id) apiParams.chef_id = params.chef_id;
      if (params.page) apiParams.page = params.page;
      if (params.limit) apiParams.limit = params.limit;
      if (params.activo !== undefined) apiParams.activo = params.activo;
    }

    console.log(
      '🔍 recetasAPI.listar - Parámetros API (snake_case):',
      apiParams
    );
    const response = await api.get('/recetas', { params: apiParams });
    console.log('📦 recetasAPI.listar - Respuesta recibida:', response.data);

    // El backend ahora devuelve { data: [...], pagination: {...} } directamente
    // sin el wrapper de success
    return response.data;
  },

  // GET /api/recetas/:id/completa - Obtener receta específica con ingredientes
  obtener: async (id: string | number): Promise<RecetaAPI> => {
    const response = await api.get(`/recetas/${id}/completa`);
    return response.data.data;
  },

  // POST /api/recetas - Crear nueva receta
  crear: async (data: CrearRecetaRequest): Promise<RecetaAPI> => {
    console.log('📤 recetasAPI.crear - Enviando datos:', data);
    console.log('📍 URL completa:', `${api.defaults.baseURL}/recetas`);

    try {
      const response = await api.post('/recetas', data);
      console.log('📥 recetasAPI.crear - Respuesta recibida:', response.data);
      return response.data.data;
    } catch (error) {
      console.error('💥 recetasAPI.crear - Error en la llamada:', error);
      throw error;
    }
  },

  // PUT /api/recetas/:id - Actualizar receta
  actualizar: async (
    id: string | number,
    data: ActualizarRecetaRequest
  ): Promise<RecetaAPI> => {
    const response = await api.put(`/recetas/${id}`, data);
    return response.data.data;
  },

  // DELETE /api/recetas/:id - Archivar receta
  eliminar: async (id: string | number): Promise<void> => {
    await api.delete(`/recetas/${id}`);
  },

  // POST /api/recetas/:id/ingredientes - Agregar ingrediente a receta
  agregarIngrediente: async (
    recetaId: string | number,
    data: AgregarIngredienteRequest
  ): Promise<IngredienteRecetaAPI> => {
    const response = await api.post(`/recetas/${recetaId}/ingredientes`, data);
    return response.data.data;
  },

  // PUT /api/recetas/:id/ingredientes/:ingredienteId - Actualizar ingrediente en receta
  actualizarIngrediente: async (
    recetaId: string | number,
    ingredienteId: string | number,
    data: ActualizarIngredienteRequest
  ): Promise<IngredienteRecetaAPI> => {
    const response = await api.put(
      `/recetas/${recetaId}/ingredientes/${ingredienteId}`,
      data
    );
    return response.data.data;
  },

  // DELETE /api/recetas/:id/ingredientes/:ingredienteId - Remover ingrediente de receta
  removerIngrediente: async (
    recetaId: string | number,
    ingredienteId: string | number
  ): Promise<void> => {
    await api.delete(`/recetas/${recetaId}/ingredientes/${ingredienteId}`);
  },

  // PATCH /api/recetas/:recetaId/ingredientes/:ingredienteRecetaId/incrementar - Incrementar cantidad
  incrementarCantidadIngrediente: async (
    recetaId: string | number,
    ingredienteRecetaId: number,
    cantidadAdicional: number
  ): Promise<IngredienteRecetaAPI> => {
    const response = await api.patch(
      `/recetas/${recetaId}/ingredientes/${ingredienteRecetaId}/incrementar`,
      { cantidad_adicional: cantidadAdicional }
    );
    return response.data.data;
  },

  // GET /api/recetas/categorias - Obtener categorías disponibles
  obtenerCategorias: async (): Promise<CategoriaReceta[]> => {
    const response = await api.get('/recetas/categorias');
    return response.data.data;
  },

  // GET /api/tipos-plato - Obtener tipos de plato disponibles desde la tabla tipos_plato
  obtenerTiposPlato: async (): Promise<TipoPlato[]> => {
    const response = await api.get('/tipos-plato');
    return response.data.data;
  },

  // GET /api/tipos-plato/:id/recetas - Recetas activas para un tipo (id),
  // usado por el wizard de cliente para listar BEBIDAS (tipo_plato_id=11).
  obtenerRecetasPorTipoPlato: async (
    tipoPlatoId: number
  ): Promise<Array<{ id: number; nombre: string }>> => {
    const response = await api.get(`/tipos-plato/${tipoPlatoId}/recetas`);
    return response.data.data;
  },
};
